/*
  <ctl-zoom> — hover magnifier for inspecting garment detail.
  Reference brief: zoom on hover, not click, to see product details.

  The cursor position sets transform-origin, so the area under the pointer is what
  magnifies — this is what makes it a loupe rather than a crop-in.

  Costs nothing until used: the high-resolution source is fetched on first hover,
  never on page load, so the grid keeps its image budget (docs/01 §8).

  Enhancement only. Without JS, or on touch, or under reduced-motion, the image is
  a normal image and every piece of information is still reachable via the card link.
*/
class CtlZoom extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('img');
    if (!this.image) return;

    this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.fine.matches || this.calm.matches) return;

    this.scale = parseFloat(this.dataset.scale) || 2.2;
    this.highResSrc = this.dataset.zoomSrc;
    this.loaded = false;
    this.frame = null;

    this.onEnter = this.onEnter.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onLeave = this.onLeave.bind(this);

    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointermove', this.onMove);
    this.addEventListener('pointerleave', this.onLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointermove', this.onMove);
    this.removeEventListener('pointerleave', this.onLeave);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /* Swap in the detail-resolution image once, on first intent to zoom. */
  loadHighRes() {
    if (this.loaded || !this.highResSrc) return;
    this.loaded = true;
    const hi = new Image();
    hi.onload = () => {
      this.image.src = this.highResSrc;
      this.image.srcset = '';
    };
    hi.src = this.highResSrc;
  }

  onEnter(event) {
    this.loadHighRes();
    this.setAttribute('zoomed', '');
    this.track(event);
  }

  onMove(event) {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.track(event);
    });
  }

  /* transform-origin follows the pointer, clamped so edges stay covered. */
  track(event) {
    const box = this.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const x = ((event.clientX - box.left) / box.width) * 100;
    const y = ((event.clientY - box.top) / box.height) * 100;
    this.image.style.transformOrigin = `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`;
    this.image.style.transform = `scale(${this.scale})`;
  }

  onLeave() {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    this.removeAttribute('zoomed');
    this.image.style.transform = '';
    this.image.style.transformOrigin = '';
  }
}

if (!customElements.get('ctl-zoom')) {
  customElements.define('ctl-zoom', CtlZoom);
}
